/**
 * useScrollPrevention Hook
 *
 * Prevents scroll propagation from sheet to body when:
 * 1. Scrolling on non-scrollable areas (overlay, title, buttons)
 * 2. Scrolling on scrollable content when at boundaries (top/bottom)
 *
 * This ensures that the body doesn't scroll when the user tries to scroll
 * beyond the sheet's content boundaries.
 *
 * @param contentRef - Ref to the scrollable content area
 * @param nonScrollableRefs - Refs to non-scrollable areas (overlay, title, buttons)
 * @param options - Configuration options
 */

import { useEffect } from 'react';
import type { RefObject } from 'react';

interface UseScrollPreventionOptions {
  /**
   * Whether to prevent touchmove events in addition to wheel events.
   * Default: false (only prevent wheel)
   *
   * Set to true for DialogView (both wheel + touch)
   * Set to false for BottomSheetView (only wheel, touch handled by drag)
   */
  preventTouch?: boolean;
}

export const useScrollPrevention = (
  contentRef: RefObject<HTMLDivElement>,
  nonScrollableRefs: RefObject<HTMLDivElement | null>[],
  options: UseScrollPreventionOptions = {}
) => {
  const { preventTouch = false } = options;

  useEffect(() => {
    const preventScrollPropagation = (e: WheelEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      const content = contentRef.current;

      // If scrolling on scrollable content area
      if (content && content.contains(target)) {
        const { scrollTop, scrollHeight, clientHeight } = content;
        const isAtTop = scrollTop === 0;
        const isAtBottom = Math.abs(scrollTop + clientHeight - scrollHeight) < 1;

        // Determine scroll direction
        const isScrollingUp = e instanceof WheelEvent ? e.deltaY < 0 : false;
        const isScrollingDown = e instanceof WheelEvent ? e.deltaY > 0 : false;

        // Prevent if at boundary and trying to scroll further
        if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
          e.preventDefault();
          e.stopPropagation();
        }
        // Allow scrolling within content bounds
      } else {
        // Scrolling on non-scrollable areas (overlay, title, buttons) - always prevent
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Get all non-scrollable elements that exist
    const elements = nonScrollableRefs
      .map(ref => ref.current)
      .filter(Boolean) as HTMLElement[];

    // Add wheel listeners to all areas
    elements.forEach(el => {
      el.addEventListener('wheel', preventScrollPropagation as EventListener, { passive: false });
    });

    // Add wheel listener to content with boundary detection
    if (contentRef.current) {
      contentRef.current.addEventListener('wheel', preventScrollPropagation as EventListener, { passive: false });
    }

    // Add touchmove listeners if enabled
    if (preventTouch) {
      elements.forEach(el => {
        el.addEventListener('touchmove', preventScrollPropagation as EventListener, { passive: false });
      });

      if (contentRef.current) {
        contentRef.current.addEventListener('touchmove', preventScrollPropagation as EventListener, { passive: false });
      }
    }

    // Cleanup
    return () => {
      elements.forEach(el => {
        el.removeEventListener('wheel', preventScrollPropagation as EventListener);
        if (preventTouch) {
          el.removeEventListener('touchmove', preventScrollPropagation as EventListener);
        }
      });

      if (contentRef.current) {
        contentRef.current.removeEventListener('wheel', preventScrollPropagation as EventListener);
        if (preventTouch) {
          contentRef.current.removeEventListener('touchmove', preventScrollPropagation as EventListener);
        }
      }
    };
  }, [contentRef, preventTouch, ...nonScrollableRefs]);
};
